import React, { useEffect, Suspense } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { HelmetProvider } from "react-helmet-async";
import { navRef } from "@/lib/nav";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { lazyRetry } from "@/lib/lazyRetry";
import { AppErrorBoundary, AdminFallback } from "@/components/AppErrorBoundary";

import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
const ProductList = lazyRetry(() => import("@/pages/ProductList"));
const ProductDetail = lazyRetry(() => import("@/pages/ProductDetail"));
const FamilyPage = lazyRetry(() => import("@/pages/FamilyPage"));
const ComposeBox = lazyRetry(() => import("@/pages/ComposeBox"));
const LimitedEditions = lazyRetry(() => import("@/pages/LimitedEditions"));
const About = lazyRetry(() => import("@/pages/About"));
const HowToOrder = lazyRetry(() => import("@/pages/HowToOrder"));
const Events = lazyRetry(() => import("@/pages/Events"));
const Gift = lazyRetry(() => import("@/pages/Gift"));
const Checkout = lazyRetry(() => import("@/pages/Checkout"));
const PaymentResult = lazyRetry(() => import("@/pages/PaymentResult"));
const OrderTracking = lazyRetry(() => import("@/pages/OrderTracking"));
const OrderReview = lazyRetry(() => import("@/pages/OrderReview"));
const Account = lazyRetry(() => import("@/pages/Account"));
const Favorites = lazyRetry(() => import("@/pages/Favorites"));
const Auth = lazyRetry(() => import("@/pages/Auth"));
const Admin = lazyRetry(() => import("@/pages/Admin"));
const Legal = lazyRetry(() => import("@/pages/Legal"));
const LocalPage = lazyRetry(() => import("@/pages/LocalPage"));
const Blog = lazyRetry(() => import("@/pages/Blog"));
const BlogPost = lazyRetry(() => import("@/pages/Blog").then((m) => ({ default: m.BlogPost })));
import MaintenanceGate, { MaintenancePreview } from "@/components/Maintenance";
import FlashBanner from "@/components/FlashBanner";
import CartDrawer from "@/components/CartDrawer";
import { livePing, startLiveHeartbeat } from "@/lib/live";

const Fallback = () => <div className="min-h-[70vh]" aria-busy="true" />;
const Page = ({ children, noFooter }) => <Layout noFooter={noFooter}><Suspense fallback={<Fallback />}>{children}</Suspense></Layout>;

function Prefetch() {
  useEffect(() => {
    const go = () => [import("@/pages/ProductList"), import("@/pages/ProductDetail"), import("@/pages/ComposeBox"), import("@/pages/Checkout"), import("@/pages/LimitedEditions"), import("@/pages/Gift")].forEach((p) => p.catch(() => {}));
    const ric = window.requestIdleCallback;
    const id = ric ? ric(go, { timeout: 5000 }) : setTimeout(go, 3500);
    return () => { if (ric) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, []);
  return null;
}

function Tracker() {
  const loc = useLocation();
  useEffect(() => startLiveHeartbeat(), []);
  useEffect(() => {
    window.scrollTo(0, 0);
    let sid = localStorage.getItem("cs_session");
    if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("cs_session", sid); }
    const path = loc.pathname;
    if (path.startsWith("/admin") || path.startsWith("/produit/")) return;
    const utm = new URLSearchParams(window.location.search).get("utm_source");
    const ref = utm ? `utm:${utm.slice(0, 40)}` : (document.referrer && !document.referrer.startsWith(window.location.origin)) ? document.referrer : "";
    api.post("/track", { path, session: sid, ref }).catch(() => {});
    livePing("view");
    if (window.gtag) window.gtag("event", "page_view", { page_path: path });
  }, [loc.pathname]);
  return null;
}

function GAInit() {
  const { settings } = useApp();
  const id = settings?.ga_measurement_id;
  useEffect(() => {
      if (!id || window.__gaLoaded) return;
      window.__gaLoaded = true;
      const s = document.createElement("script");
      s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", id);
  }, [id]);
  return null;
}

function NavBridge() {
  const navigate = useNavigate();
  useEffect(() => { navRef.navigate = navigate; }, [navigate]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <AppProvider>
      <Toaster position="top-right" richColors toastOptions={{ style: { fontFamily: "Plus Jakarta Sans" } }} />
      <BrowserRouter>
        <NavBridge />
        <Tracker />
        <GAInit />
        <Prefetch />
        <MaintenanceGate>
        <FlashBanner />
        <CartDrawer />
        <AppErrorBoundary>
        <Routes>
          <Route path="/" element={<Page><Home /></Page>} />
          <Route path="/cookies" element={<Page><ProductList type="cookie" /></Page>} />
          <Route path="/brownies" element={<Page><ProductList type="brownie" /></Page>} />
          <Route path="/cupcookies" element={<Page><ProductList type="cupcookie" /></Page>} />
          <Route path="/box" element={<Page><ComposeBox /></Page>} />
          <Route path="/composer-ma-box" element={<Page><ComposeBox /></Page>} />
          <Route path="/editions-limitees" element={<Page><LimitedEditions /></Page>} />
          <Route path="/produit/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="/cookies/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="/brownies/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="/cupcookies/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="/box/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="/cookies-palaiseau" element={<Page><LocalPage slug="palaiseau" /></Page>} />
          <Route path="/cookies-entreprises-essonne" element={<Page><LocalPage slug="entreprises" /></Page>} />
          <Route path="/blog" element={<Page><Blog /></Page>} />
          <Route path="/blog/:slug" element={<Page><BlogPost /></Page>} />
          <Route path="/a-propos" element={<Page><About /></Page>} />
          <Route path="/comment-commander" element={<Page><HowToOrder /></Page>} />
          <Route path="/evenements" element={<Page><Events /></Page>} />
          <Route path="/offrir" element={<Page><Gift /></Page>} />
          <Route path="/panier" element={<Page noFooter><Checkout /></Page>} />
          <Route path="/commander" element={<Page noFooter><Checkout /></Page>} />
          <Route path="/payment/success" element={<Page><PaymentResult /></Page>} />
          <Route path="/payment/cancel" element={<Page><PaymentResult cancel /></Page>} />
          <Route path="/suivi" element={<Page><OrderTracking /></Page>} />
          <Route path="/suivi/:orderNumber" element={<Page><OrderTracking /></Page>} />
          <Route path="/avis/:orderNumber" element={<Page><OrderReview /></Page>} />
          <Route path="/compte" element={<Page><Account /></Page>} />
          <Route path="/favoris" element={<Page><Favorites /></Page>} />
          <Route path="/connexion" element={<Page><Auth /></Page>} />
          <Route path="/mentions-legales" element={<Page><Legal type="mentions" /></Page>} />
          <Route path="/cgv" element={<Page><Legal type="cgv" /></Page>} />
          <Route path="/confidentialite" element={<Page><Legal type="privacy" /></Page>} />
          <Route path="/politique-cookies" element={<Page><Legal type="cookies" /></Page>} />
          <Route path="/livraison-retrait" element={<Page><Legal type="delivery" /></Page>} />
          <Route path="/allergenes" element={<Page><Legal type="allergens" /></Page>} />
          <Route path="/mediation" element={<Page><Legal type="mediation" /></Page>} />
          <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><Admin /></Suspense>} />
          <Route path="/apercu-maintenance" element={<MaintenancePreview />} />
          <Route path="/:family" element={<Page><FamilyPage /></Page>} />
          <Route path="/:family/:id" element={<Page><ProductDetail /></Page>} />
          <Route path="*" element={<Page><NotFound /></Page>} />
        </Routes>
        </AppErrorBoundary>
        </MaintenanceGate>
      </BrowserRouter>
    </AppProvider>
    </HelmetProvider>
  );
}

export default App;
